import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { ChatMessage } from '@app/interfaces';
import { ConversationStateService } from '../conversation-state/conversation-state.service';
import { MessageOperationsService } from '../message-operations/message-operations.service';
import { SSEStreamHandlerService, SSERawEvent } from '../sse-stream/sse-stream-handler.service';
import { StreamEventRouterService } from '../stream-events/stream-event-router.service';
import { ConversationPersistenceService } from '../conversation-persistence/conversation-persistence.service';
import { ConversationHistoryStorageService } from '../conversation-history/conversation-history-storage.service';
import { CognitiveStateManagerService } from './cognitive-state-manager.service';
import { ConversationMessage, ConversationState } from './conversation-message.interface';
import { FeedbackSentiment, FeedbackCategory } from './feedback-state.interface';

// Re-export for components
export type { ConversationState, ConversationMessage, SSERawEvent };

/**
 * Refactored RealtimeSessionStreamService
 * Now orchestrates specialized services instead of doing everything itself.
 * This reduces the service from 1100+ lines to ~300 lines.
 */
@Injectable()
export class RealtimeSessionStreamService {
    private currentRequestId: string | null = null;

    // Final response span tracking
    private insideFinalResponseSpan = false;

    // Error handling state - prevent duplicate error messages
    private hasHandledError = false;

    // Datasheet request timing for latency tracking
    private datasheetRequestTimestamps = new Map<number, number>();

    constructor(
        private readonly conversationState: ConversationStateService,
        private readonly messageOps: MessageOperationsService,
        private readonly sseHandler: SSEStreamHandlerService,
        private readonly eventRouter: StreamEventRouterService,
        private readonly persistence: ConversationPersistenceService,
        private readonly conversationHistory: ConversationHistoryStorageService,
        private readonly cognitiveStateManager: CognitiveStateManagerService
    ) {
        this.setupEventHandlers();
    }

    // ==================== PUBLIC OBSERVABLES ====================

    get conversation$(): Observable<ConversationState> {
        return this.conversationState.state$;
    }

    get conversationForAgent$(): Observable<ChatMessage[]> {
        return this.conversationState.messagesForAgent$;
    }

    get isStreaming$(): Observable<boolean> {
        return this.conversationState.isStreaming$;
    }

    get isConnected$(): Observable<boolean> {
        return this.sseHandler.isConnected$;
    }

    get raw$(): Observable<SSERawEvent> {
        return this.sseHandler.rawEvents$;
    }

    get streamingText$(): Observable<string> {
        return this.conversationState.state$.pipe(map((state) => state.currentStreamingText || ''));
    }

    get cognitiveUIState$(): Observable<any> {
        return this.cognitiveStateManager.state$.pipe(
            map((state) => {
                if (!state) return null;
                return {
                    cognitiveTree: state.rootSpans,
                    totalEvents: state.totalEvents,
                    nodesExecuted: state.nodesExecuted,
                    toolsExecuted: state.toolsExecuted
                };
            })
        );
    }

    // ==================== CONVERSATION MANAGEMENT ====================

    /**
     * Start conversation stream
     */
    async startConversationStream(messages: ChatMessage[]): Promise<void> {
        // Check if already streaming
        if (this.sseHandler.isCurrentlyConnected) {
            console.log('⚠️ Stream already in progress');
            return;
        }

        // Ensure session exists
        this.ensureSession();

        // Generate request ID
        this.currentRequestId = this.messageOps.generateRequestId();

        // Create assistant placeholder for streaming
        this.messageOps.createAssistantPlaceholder(this.currentRequestId);

        // Reset cognitive processor
        this.cognitiveStateManager.resetState();

        // Reset final response span tracking
        this.insideFinalResponseSpan = false;

        // Reset error handling state for this stream
        this.hasHandledError = false;

        // Start the SSE stream
        await this.sseHandler.startStream(messages);
    }

    /**
     * Add user message
     */
    addUserMessage(content: string): ConversationMessage {
        return this.messageOps.addUserMessage(content);
    }

    /**
     * Add assistant message (for errors, etc.)
     */
    addAssistantMessageToConversation(content: string, requestId?: string): ConversationMessage {
        const message = this.messageOps.addAssistantMessage(content, requestId);
        this.persistence.saveConversation(this.conversationState.messages);
        return message;
    }

    /**
     * Add feedback prompt
     */
    addFeedbackPromptToConversation(targetRequestId: string): ConversationMessage | null {
        return this.messageOps.addFeedbackPrompt(targetRequestId);
    }

    /**
     * Mark feedback as submitted
     */
    markFeedbackSubmitted(
        targetRequestId: string,
        sentiment?: FeedbackSentiment,
        data?: {
            categories?: FeedbackCategory[];
            reasons?: string[];
            comment?: string;
            userEmail?: string;
        }
    ): void {
        this.messageOps.markFeedbackSubmitted(targetRequestId, sentiment, data);
        this.persistence.saveConversation(this.conversationState.messages);
    }

    /**
     * Update last message cognitive state
     */
    updateLastMessageCognitiveState(cognitiveState: any): void {
        this.messageOps.updateLastMessageCognitiveState(cognitiveState);
        this.persistence.saveConversation(this.conversationState.messages);

        // Notify conversation history service
        this.conversationHistory.changeSessionsList();
    }

    /**
     * Load conversation from storage
     */
    loadConversationFromStorage(messages: ChatMessage[], sessionId?: number): void {
        // console.log('📂 Loading conversation from storage');

        let conversationMessages: ConversationMessage[];

        if (sessionId) {
            conversationMessages = this.persistence.loadConversation(sessionId);
        } else {
            // Convert provided messages
            conversationMessages = messages.map(
                (msg) =>
                    ({
                        ...msg,
                        messageType: (msg as any).messageType || (msg.role === 'user' ? 'user' : 'assistant'),
                        messageId: (msg as any).messageId || this.messageOps.generateMessageId(),
                        timestamp: (msg as any).timestamp || Date.now()
                    }) as ConversationMessage
            );
        }

        this.conversationState.setMessages(conversationMessages);
        this.conversationState.setStreamingState(false);
    }

    /**
     * Reset conversation
     */
    resetConversation(): void {
        // console.log('🧹 Resetting conversation');
        this.conversationState.reset();
        this.cognitiveStateManager.resetState();
        this.sseHandler.abort();
        this.currentRequestId = null;
    }

    /**
     * Clear conversation
     */
    clearConversation(): void {
        this.conversationState.reset();
    }

    /**
     * Abort stream
     */
    abortStream(): void {
        this.sseHandler.abort();
        this.completeStream();
    }

    // ==================== PRIVATE METHODS ====================

    /**
     * Setup event handlers for stream processing
     */
    private setupEventHandlers(): void {
        // Handle raw SSE events
        this.sseHandler.rawEvents$.subscribe((event) => {
            this.eventRouter.processRawEvent(event);
        });

        // Handle cognitive events
        this.eventRouter.cognitiveEvents$.subscribe((event) => {
            this.processCognitiveEvent(event);
        });

        // Handle token events
        this.eventRouter.tokenEvents$.subscribe((event) => {
            this.processTokenEvent(event);
        });

        // Handle datasheet events
        this.eventRouter.datasheetEvents$.subscribe((event) => {
            this.processDatasheetEvent(event);
        });

        // Handle completion events
        combineLatest([this.eventRouter.completionEvents$, this.sseHandler.completion$]).subscribe(() => {
            this.completeStream();
        });

        // Handle errors
        this.sseHandler.errors$.subscribe((error) => {
            this.handleStreamError(error);
        });
    }

    /**
     * Process cognitive event
     */
    private processCognitiveEvent(event: any): void {
        // Check for final response span markers
        this.checkForFinalResponseSpanMarker(event);

        // SIMPLIFIED: Use only ONE cognitive service
        this.cognitiveStateManager.processEvent({
            type: event.custom_outputs?.event_type || event.type,
            custom_outputs: event.custom_outputs,
            delta: event.delta,
            chunk_id: event.chunk_id,
            item_id: event.item_id
        });
    }

    /**
     * Process token event - Only process tokens inside final response span
     */
    private processTokenEvent(event: any): void {
        const customOutputs = (event as any).custom_outputs;

        if (customOutputs?.event_type === 'response.llm.token' && this.insideFinalResponseSpan) {
            this.messageOps.updateStreamingMessageContent(event.delta || '');
        }
    }

    /**
     * Check for final response span markers (content-based until backend implements explicit events)
     */
    private checkForFinalResponseSpanMarker(event: any): void {
        const customOutputs = event.custom_outputs || {};

        // Content-based detection (until backend implements explicit boundary events)
        const eventType = customOutputs.event_type;
        const delta = event.delta || '';

        // Start: When generating ANY response with LLM (extensible pattern for all request types)
        if (eventType === 'craig.cognitive.tool.update' && this.isLLMGenerationStart(delta)) {
            this.insideFinalResponseSpan = true;
        }
        // End: When cognitive tool completes
        else if (eventType === 'craig.cognitive.tool.complete') {
            this.insideFinalResponseSpan = false;
        }
    }

    /**
     * Extensible pattern matching for LLM generation start
     * Supports any request type (DETAIL, COMPARE, ALTERNATE, TECHNICAL, etc.)
     */
    private isLLMGenerationStart(delta: string): boolean {
        if (!delta) return false;

        // Extensible patterns that match all request types' LLM generation triggers
        const llmGenerationPatterns = [
            // Standard LLM generation patterns
            /Generating\s+\w+\s+response\s+with\s+LLM/i, // "Generating [type] response with LLM"
            /Generating\s+response\s+with\s+LLM/i, // "Generating response with LLM"
            /Generating\s+response/i, // "Generating response with LLM"
            /Generating\s+response/i, // "Generating response with LLM"
            /response\s+with\s+LLM/i, // Generic "response with LLM"
            /Starting\s+LLM\s+generation/i, // "Starting LLM generation"
            /Begin\s+LLM\s+response/i, // "Begin LLM response"

            /Generating\s+response/i,
            /Generating\s+final/i,
            /final\s+response/i,

            // Request-type specific workflow triggers
            /Starting\s+alternative\s+part\s+analysis\s+workflow/i, // ALTERNATE requests
            /alternative\s+part\s+analysis\s+workflow/i, // Generic alternative workflow
            /Generating\s+alternate/i, // "Generating alternate..."
            /alternate\s+recommendations/i, // "alternate recommendations"

            // CRITICAL FIX: Compare and Detail workflow patterns
            /Generating\s+comparison\s+response/i, // "Generating comparison response" - COMPARE workflows
            /Generating\s+detail\s+response/i, // "Generating detail response" - DETAIL workflows
            /Generating\s+detailed\s+response/i, // "Generating detailed response" - DETAIL variants
            /Generating\s+comparative\s+response/i // "Generating comparative response" - COMPARE variants
        ];

        return llmGenerationPatterns.some((pattern) => pattern.test(delta));
    }

    /**
     * Complete the stream
     */
    private completeStream(): void {
        // console.log('🏁 Completing stream');

        // CRITICAL FIX: Save cognitive state BEFORE ending streaming
        const cognitiveState = this.cognitiveStateManager.getStateSnapshot();
        // console.log('💾 Cognitive state at completion:', {
        //     hasState: !!cognitiveState,
        //     rootSpans: cognitiveState?.rootSpans?.length || 0,
        //     totalEvents: cognitiveState?.totalEvents || 0
        // });

        if (cognitiveState && (cognitiveState.rootSpans.length > 0 || cognitiveState.totalEvents > 0)) {
            // console.log('🚀 Saving cognitive state to message');
            this.messageOps.updateLastMessageCognitiveState(cognitiveState);

            // Trigger conversation persistence
            this.persistence.saveConversation(this.conversationState.messages);
        }

        // Check if this completion is from an error
        const isErrorCompletion = this.hasHandledError;

        // Mark message as complete (or error if applicable)
        if (isErrorCompletion) {
            // Don't mark as complete, the error handler already did it
            // console.log('✅ Stream completed with error - no feedback prompt');
        } else {
            this.messageOps.completeStreamingMessage();
        }

        // End cognitive streaming
        this.cognitiveStateManager.endStreaming();

        // Only add feedback prompt for successful responses, not errors
        if (this.currentRequestId && !isErrorCompletion) {
            this.messageOps.addFeedbackPrompt(this.currentRequestId);
            // console.log('✅ Stream completed successfully - feedback prompt added');
        }

        // Reset state
        this.currentRequestId = null;
    }

    /**
     * Handle stream error
     * Only processes the first error to prevent duplicate error messages
     * Uses context-aware error messages from AuthClientService
     */
    private handleStreamError(error: Error): void {
        // Ignore duplicate errors - SSE stream can emit multiple error events
        if (this.hasHandledError) {
            console.log('⚠️ Ignoring duplicate stream error');
            return;
        }

        console.error('❌ Stream error:', error);

        // Mark that we've handled an error for this stream
        this.hasHandledError = true;

        // Use context-aware error message from AuthClientService
        // This provides specific messages for 401, 403, 5XX, network errors, etc.
        const userFacingMessage =
            error.message || 'Sorry, there was an error processing your request. Please try again.';

        // console.log('📝 Displaying context-aware error message:', userFacingMessage);

        // Update the placeholder message with context-aware error content
        // This replaces the empty placeholder created at stream start
        this.messageOps.updateStreamingMessageContent(userFacingMessage);

        // Mark the message as error - this prevents feedback prompt from showing
        this.messageOps.markStreamingMessageAsError(userFacingMessage);

        this.completeStream();
    }

    /**
     * Ensure conversation session exists
     */
    private ensureSession(): void {
        let sessionId = this.conversationHistory.getCurrentConversationId();
        if (sessionId === -1) {
            // console.log('🆕 Starting new conversation session');
            this.conversationHistory.startNewSession();
        }
    }

    /**
     * Process datasheet events and attach them to the current assistant message
     */
    private processDatasheetEvent(event: any): void {
        let index = this.conversationState.streamingMessageIndex;

        if (index === undefined || index < 0) {
            const messages = this.conversationState.messages;
            index = [...messages]
                .map((message, messageIndex) => ({ message, messageIndex }))
                .reverse()
                .find(
                    ({ message }) =>
                        message.role === 'assistant' &&
                        message.messageType !== 'feedback_prompt'
                )?.messageIndex;
        }

        if (index === undefined || index < 0) {
            return;
        }

        const message = this.conversationState.messages[index];
        if (!message) {
            return;
        }

        const eventType = event.custom_outputs?.event_type;
        const currentState = message.datasheetState || {
            status: 'idle',
            items: []
        };

        if (eventType === 'craig.datasheets.status') {
            this.datasheetRequestTimestamps.set(index, Date.now());
            this.trackDatasheetRequest(index);

            this.conversationState.updateMessageByIndex(index, {
                datasheetState: {
                    ...currentState,
                    status: 'loading'
                }
            });

            this.persistence.saveConversation(this.conversationState.messages);

            return;
        }

        if (eventType === 'craig.datasheets.result') {
            const payload = event.custom_outputs?.datasheets || {};

            const startTime = this.datasheetRequestTimestamps.get(index);
            const latencyMs = startTime ? Date.now() - startTime : undefined;

            this.trackDatasheetResult(payload, latencyMs, index);

            if (startTime) {
                this.datasheetRequestTimestamps.delete(index);
            }

            this.conversationState.updateMessageByIndex(index, {
                datasheetState: {
                    status: payload.status || 'none',
                    items: payload.items || []
                }
            });

            this.persistence.saveConversation(this.conversationState.messages);
        }
    }

    /**
     * Track datasheet request initiation in New Relic
     */
    private trackDatasheetRequest(messageIndex: number): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            newrelic.addPageAction('datasheet_request_initiated', {
                timestamp: new Date().toISOString(),
                conversationId: this.conversationHistory.getCurrentConversationId(),
                messageIndex: messageIndex,
                featureUsed: 'datasheet_retrieval'
            });
        }
    }

    /**
     * Track datasheet results in New Relic with comprehensive metrics
     */
    private trackDatasheetResult(
        payload: any,
        latencyMs: number | undefined,
        messageIndex: number
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            const items = payload.items || [];
            const status = payload.status || 'none';

            // Determine failure reason category
            let failureReason = 'none';
            if (status === 'unavailable') {
                // Backend returned unavailable status - could be auth or upstream error
                const message = payload.message?.toLowerCase() || '';
                if (message.includes('auth') || message.includes('unauthorized')) {
                    failureReason = 'auth';
                } else if (
                    message.includes('upstream') ||
                    message.includes('service') ||
                    message.includes('error')
                ) {
                    failureReason = 'upstream_error';
                } else {
                    failureReason = 'unknown_error';
                }
            } else if (status === 'none') {
                failureReason = 'none_found';
            }

            // Extract part information from items
            const uniqueManufacturers = new Set(
                items.map((item: any) => item.manufacturerName).filter(Boolean)
            ).size;

            // Categorize latency for easier analysis
            let latencyBucket = 'unknown';
            if (latencyMs !== undefined) {
                if (latencyMs < 500) latencyBucket = 'fast';
                else if (latencyMs < 1500) latencyBucket = 'medium';
                else if (latencyMs < 3000) latencyBucket = 'slow';
                else latencyBucket = 'very_slow';
            }

            newrelic.addPageAction('datasheet_result_received', {
                timestamp: new Date().toISOString(),
                conversationId: this.conversationHistory.getCurrentConversationId(),
                messageIndex: messageIndex,

                // Feature usage
                featureUsed: 'datasheet_retrieval',

                // Quantity metrics
                partsRequested: items.length,
                datasheetsFound: status === 'found' ? items.length : 0,
                linksRendered: items.length,
                uniqueManufacturers: uniqueManufacturers,

                // Status and failure tracking
                status: status,
                failureReason: failureReason,
                statusMessage: payload.message || '',

                // Success indicators
                hasResults: status === 'found' && items.length > 0,
                isEmpty: status === 'none',
                isError: status === 'unavailable',

                // Latency tracking
                latencyMs: latencyMs,
                hasLatencyData: latencyMs !== undefined,
                latencyBucket: latencyBucket
            });
        }
    }
}
